'use memo';
import { createFileRoute } from '@tanstack/react-router';

import {
  StatCard,
  StatCardContent,
  StatCardPrimaryValue,
  StatCardSecondaryValue,
  StatCardTable,
  StatCardTableRow,
  StatCardTitle,
  StatCardValueContainer,
} from '@/components/Card/StatCard';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetIssueOverview } from '@/features/stacktrace/issues/-query';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { cn } from '@/lib/cn';
import { stacktraceNS, useTranslation } from '@/lib/i18n';

import { DailyTicketTrendChart } from './-components/DailyTicketTrendChart';

/**
 * 不具合 概要タブ ダッシュボード。
 *
 * playbook §3.15 (lazy 分離 cascade 回避) に従い 1 ファイル構成
 * (createFileRoute + component 直結) で配置。
 *
 * 推移グラフは chart.js の重い generic を持つため、module 境界で型推論を切る目的で
 * `-components/DailyTicketTrendChart` に分離 (playbook §3.16/§3.17)。
 */
export const Route = createFileRoute('/_stacktrace/issues/_layout/overview/')({
  component: IssueOverviewPage,
});

function IssueOverviewPage() {
  const { t } = useTranslation(stacktraceNS, {
    keyPrefix: 'issues.overview',
  });
  useDocumentTitle(t('pageTitle'));
  // releaseId / from / to は当面未指定 (BE デフォルトの全件・直近 N 日を使用)
  const { data, isPending } = useGetIssueOverview();

  if (isPending || !data) {
    return (
      <VStack className="grow gap-4 px-12 py-5">
        <Text variant="h2">{t('pageTitle')}</Text>
        <Skeleton className="h-32 w-full max-w-[480px] rounded-lg" />
      </VStack>
    );
  }

  return (
    <VStack className="grow gap-4 px-12 py-5">
      <Text variant="h2">{t('pageTitle')}</Text>
      <div className="grid grid-cols-4 gap-6 auto-rows-[360px]">
        <StatCard className="w-full h-full">
          <StatCardTitle>{t('issueCount.title')}</StatCardTitle>
          <StatCardContent>
            <StatCardValueContainer>
              <StatCardPrimaryValue>
                {data.issueCount.total}
              </StatCardPrimaryValue>
            </StatCardValueContainer>
            <HStack className="gap-3 mt-2">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('issueCount.unresolved')}: {data.issueCount.unresolved}
                {t('countSuffix')}
              </Text>
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                /
              </Text>
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('issueCount.resolved')}: {data.issueCount.resolved}
                {t('countSuffix')}
              </Text>
            </HStack>
          </StatCardContent>
        </StatCard>
        <StatCard className="w-full h-full">
          <StatCardTitle>{t('enhancementCount.title')}</StatCardTitle>
          <StatCardContent>
            <StatCardValueContainer>
              <StatCardPrimaryValue>
                {data.enhancementCount.total}
              </StatCardPrimaryValue>
            </StatCardValueContainer>
            <HStack className="gap-3 mt-2">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('enhancementCount.unresolved')}:{' '}
                {data.enhancementCount.unresolved}
                {t('countSuffix')}
              </Text>
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                /
              </Text>
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('enhancementCount.resolved')}:{' '}
                {data.enhancementCount.resolved}
                {t('countSuffix')}
              </Text>
            </HStack>
          </StatCardContent>
        </StatCard>
        <StatCard className="w-full h-full">
          <StatCardTitle>{t('severityDistribution.title')}</StatCardTitle>
          <StatCardContent>
            <StatCardTable>
              {(
                [
                  {
                    label: t('severityDistribution.critical'),
                    value: data.severityDistribution.critical,
                    markerClass: 'bg-error',
                  },
                  {
                    label: t('severityDistribution.high'),
                    value: data.severityDistribution.high,
                    // 重大より一段薄い赤系統で「大」を表現
                    markerClass: 'bg-error-muted-fill',
                  },
                  {
                    label: t('severityDistribution.medium'),
                    value: data.severityDistribution.medium,
                    markerClass: 'bg-warning',
                  },
                  {
                    label: t('severityDistribution.low'),
                    value: data.severityDistribution.low,
                    markerClass: 'bg-neutral-muted-fill',
                  },
                ] as const
              ).map((row) => (
                <StatCardTableRow key={row.label}>
                  <HStack className="items-center gap-2 whitespace-nowrap">
                    <span
                      className={cn(
                        'size-2 rounded-full shrink-0',
                        row.markerClass,
                      )}
                    />
                    <Text
                      variant="label"
                      className="text-neutral-on-fill whitespace-nowrap"
                    >
                      {row.label}
                    </Text>
                  </HStack>
                  <span />
                  <Text
                    variant="label"
                    className="font-mono text-neutral-on-fill"
                  >
                    {row.value}
                  </Text>
                </StatCardTableRow>
              ))}
            </StatCardTable>
          </StatCardContent>
        </StatCard>
        {/* 平均未解決日数: 先週比はプラス (悪化) を error、マイナス (改善) を
            success、0 を neutral で色分け */}
        <StatCard className="w-full h-full">
          <StatCardTitle>{t('averageUnresolvedDays.title')}</StatCardTitle>
          <StatCardContent>
            <StatCardValueContainer>
              <StatCardPrimaryValue>
                {data.averageUnresolvedDays.days}
              </StatCardPrimaryValue>
              <StatCardSecondaryValue>
                {t('averageUnresolvedDays.daySuffix')}
              </StatCardSecondaryValue>
            </StatCardValueContainer>
            <Text
              variant="labelSmall"
              className={cn(
                'mt-2',
                data.averageUnresolvedDays.weekOverWeekDelta > 0
                  ? 'text-error'
                  : data.averageUnresolvedDays.weekOverWeekDelta < 0
                    ? 'text-success'
                    : 'text-neutral-muted-on-fill',
              )}
            >
              {data.averageUnresolvedDays.weekOverWeekDelta > 0
                ? '+'
                : data.averageUnresolvedDays.weekOverWeekDelta < 0
                  ? '−'
                  : '±'}
              {Math.abs(data.averageUnresolvedDays.weekOverWeekDelta)}
              {t('averageUnresolvedDays.daySuffix')}{' '}
              {t('averageUnresolvedDays.weekOverWeek')}
            </Text>
          </StatCardContent>
        </StatCard>
      </div>
      <VStack className="grow min-h-0">
        <DailyTicketTrendChart data={data.dailyTicketCounts} />
      </VStack>
    </VStack>
  );
}
